import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ─── Request Interceptor ────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    // Generate and attach Session ID for guest users
    let sessionId = localStorage.getItem('Rayoptics-session-id')
    if (!sessionId) {
      sessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36)
      localStorage.setItem('Rayoptics-session-id', sessionId)
    }
    config.headers['X-Session-Id'] = sessionId

    // Get token from localStorage (persisted by Zustand)
    const authData = localStorage.getItem('Rayoptics-auth')
    if (authData) {
      try {
        const parsed = JSON.parse(authData)
        const token = parsed?.state?.token
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch {
        // ignore parse errors
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// ─── Response Interceptor ───────────────────────────────────
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })
  failedQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Try to refresh token
        const authData = localStorage.getItem('Rayoptics-auth')
        const parsed = authData ? JSON.parse(authData) : null
        const refreshToken = parsed?.state?.refreshToken

        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          })

          const newToken = response.data.access_token
          const newRefreshToken = response.data.refresh_token

          // Update stored tokens — the backend rotates the refresh token on every use,
          // so the old one stops working after this and must be replaced too.
          if (parsed?.state) {
            parsed.state.token = newToken
            if (newRefreshToken) parsed.state.refreshToken = newRefreshToken
            localStorage.setItem('Rayoptics-auth', JSON.stringify(parsed))
          }

          api.defaults.headers.common.Authorization = `Bearer ${newToken}`
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          processQueue(null, newToken)
          return api(originalRequest)
        }

        // No refresh token to try — the session is unrecoverable, so log out cleanly
        // instead of leaving the UI stuck "signed in" while every request 401s.
        processQueue(error, null)
        localStorage.removeItem('Rayoptics-auth')
        window.location.href = '/login'
        return Promise.reject(error)
      } catch (refreshError) {
        processQueue(refreshError, null)
        // Clear auth state on refresh failure
        localStorage.removeItem('Rayoptics-auth')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api
